/**
 * v3.21-1600 · Global banners for the Partner Content CMS.
 *
 *   • RequiredDocsBanner — red banner listing every required document
 *     the current caller has NOT acknowledged.  Persists across every
 *     page until the caller acknowledges.
 *
 *   • AnnouncementsBanner — dismissable notices published from the CMS.
 *     Dismiss state is kept in localStorage per user/announcement so
 *     it does not spam the same partner every page load.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, X, Megaphone } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const DISMISS_KEY = 'ptc.cms.announcements.dismissed';

function _loadDismissed() {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}'); } catch { return {}; }
}
function _saveDismissed(m) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify(m)); } catch { /* noop */ }
}

export function RequiredDocsBanner() {
  const [items, setItems] = useState([]);
  const [busy, setBusy]   = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/content-library/required-outstanding');
      setItems(r.data?.items || []);
    } catch { /* logged-out or endpoint unavailable */ }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);
  if (!items.length) return null;
  const ack = async (r) => {
    setBusy(r.id);
    try { await api.post(`/content-library/resources/${r.id}/acknowledge`); toast.success(`Acknowledged: ${r.title}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Ack failed'); }
    finally { setBusy(null); }
  };
  const download = async (r) => {
    try {
      const resp = await api.get(`/content-library/resources/${r.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a'); a.href = url; a.download = (r.current_version && r.current_version.original_filename) || `${r.title}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };
  return (
    <div className="bg-rose-50 border-b border-rose-300 px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="required-docs-banner">
      <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" />
      <span className="text-sm font-semibold text-rose-800">
        NEW REQUIRED DOCUMENT{items.length > 1 ? 'S' : ''} · {items.length}
      </span>
      <div className="flex flex-wrap gap-2 flex-1 min-w-0">
        {items.slice(0, 3).map((r) => (
          <span key={r.id} className="flex items-center gap-2 bg-white/70 border border-rose-200 rounded px-2 py-1 text-xs" data-testid={`required-item-${r.id}`}>
            <span className="font-medium text-rose-800 truncate max-w-[240px]">{r.title}</span>
            <button onClick={() => download(r)} className="text-rose-700 underline text-[11px]" data-testid={`required-view-${r.id}`}>View / Download</button>
            <Button size="sm" onClick={() => ack(r)} disabled={busy === r.id} className="bg-rose-600 hover:bg-rose-700 text-white h-6 px-2 text-[11px]" data-testid={`required-ack-${r.id}`}>
              {busy === r.id ? '…' : 'Mark as Read'}
            </Button>
          </span>
        ))}
        {items.length > 3 && (
          <Link to="/partner-resource-center" className="text-xs text-rose-700 underline">+ {items.length - 3} more</Link>
        )}
      </div>
    </div>
  );
}

export function AnnouncementsBanner() {
  const [items, setItems]     = useState([]);
  const [dismissed, setDismissed] = useState(_loadDismissed());
  const load = useCallback(async () => {
    try {
      const r = await api.get('/content-library/announcements');
      setItems(r.data?.items || []);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 120000); return () => clearInterval(id); }, [load]);
  const visible = items.filter((a) => !dismissed[a.id]);
  if (!visible.length) return null;
  const dismiss = (a) => {
    const next = { ...dismissed, [a.id]: new Date().toISOString() };
    setDismissed(next); _saveDismissed(next);
  };
  return (
    <div className="bg-amber-50 border-b border-amber-300 px-4 py-2 space-y-1" data-testid="announcements-banner">
      {visible.slice(0, 2).map((a) => (
        <div key={a.id} className="flex items-start gap-3" data-testid={`announcement-${a.id}`}>
          <Megaphone className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900">{a.title}</div>
            {a.announcement_body && <div className="text-xs text-amber-800/90 whitespace-pre-wrap">{a.announcement_body}</div>}
          </div>
          {a.announcement_dismissable !== false && (
            <button onClick={() => dismiss(a)} className="text-amber-700 hover:text-amber-900" data-testid={`announcement-dismiss-${a.id}`}>
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
